import React, { useState, useCallback, useMemo, useRef } from 'react';
import Modal from '../modal/Modal'

const AreaContactsRoot = ({ areas, bgImage }) => {
    const [activeItemId, setActiveItemId] = useState(null);
    const map = useRef(null);
    const [mapSizes, setMapSizes] = useState({ width: 0, height: 0 });

    React.useEffect(() => {
        const mapContainer = map?.current;

        const mapRect = mapContainer?.getBoundingClientRect();
        const mapWidth = mapRect?.width ?? 0;
        const mapHeight = mapRect?.height ?? 0;

        setMapSizes({ width: mapWidth, height: mapHeight });

    }, [map.current])

    const getGroupedData = useCallback((array, key) => {
        return array.reduce((prev, current) => {
            (prev[current[key]] = prev[current[key]] || []).push(current);
            return prev;
        }, {});
    }, []);

    const groupedAreas = useMemo(() => areas ? getGroupedData(areas, 'Area') : [], [areas, getGroupedData]);

    const handleContactClick = useCallback((index) => {
        setActiveItemId(index);
    }, []);

    const handleClose = useCallback(() => {
        setActiveItemId(null);
    }, []);

    const getModalContent = useCallback((key) => {
        const area = groupedAreas[key];

        return (
                    <div className="area-contacts__modal">
                        <h2 className="area-contacts__modal-title">
                            <span>
                                {key}
                            </span>
                        </h2>
                        <div className="area-contacts__modal-wrapper">
                            <ul className="area-contacts__list">
                                {area.map((contact, index) => {
                                    return (
                                        <li key={index} className="area-contacts__list-item">
                                            <address className="area-contacts__card" itemScope="" itemType="https://schema.org/ContactPoint">
                                                <header>
                                                    {contact.AreaDescription ?
                                                        <h3 className="area-contacts__card-region">
                                                            <span>
                                                                {contact.AreaDescription}
                                                            </span>
                                                        </h3> : null}
        
                                                    <figure className="area-contacts__card-figure">
                                                        <img src={contact.Image} alt={"A picture of" + contact.Name} />
                                                    </figure>
        
                                                    <h4 className="area-contacts__card-name" itemProp="name">
                                                        {contact.Name}
                                                    </h4>
        
                                                    {contact.Title ?
                                                        <strong className="area-contacts__card-title" itemProp="jobTitle">
                                                            {contact.Title}
                                                        </strong> : null}
                                                </header>
        
                                                <ul>
                                                    {contact.Email ?
                                                        <li className="area-contacts__card-item">
                                                            <a className="area-contacts__card-item-content" href={"mailto:" + contact.Email}>
                                                                {contact.Email}
                                                            </a>
                                                        </li> : null}
                                                    {contact.Mobile ?
                                                        <li className="area-contacts__card-item">
                                                            <a className="area-contacts__card-item-content" href={"tel: " + contact.Mobile} itemProp="telephone">
                                                                {contact.Mobile}
                                                            </a>
                                                        </li> : null}
        
                                                    {contact.Linkedin ?
                                                        <li className="area-contacts__card-item">
                                                            <a className="area-contacts__card-item-content area-contacts__card-item-content--linkedin" href={contact.Linkedin} itemProp="url">
                                                                Linkedin
                                                            </a>
                                                        </li> : null}
                                                </ul>
                                            </address>
                                        </li>
                                    );
                                })}
                            </ul>
                        </div>
                    </div>
        )
    }, [groupedAreas]);

    const calculateButtonPosition = (area, index) => {
        const button = map?.current?.querySelector(`.area-contacts__area-item-${index + 1}`);

        const normalizedLatitude = (((area.Latitude * -1) + 90) / 180) * 100;
        const normalizedLongitude = ((area.Longitude + 180) / 360) * 100;

        const buttonSizes = button?.getBoundingClientRect();

        const buttonWidth = buttonSizes?.width ?? 100;
        const buttonHeight = buttonSizes?.height ?? 100;

        const x = (normalizedLongitude / 100) * mapSizes.width;
        const y = (normalizedLatitude / 100) * mapSizes.height;

        const displayX = x > buttonWidth ? x - buttonWidth : x;
        const displayY = y > buttonHeight ? y + (2 * buttonHeight) : y;

        return { x: displayX, y: displayY };
    };

    const styles = useMemo(() => ({ backgroundImage: `url(${bgImage})` }), [bgImage]);

    const positions = useMemo(() => {
        return Object.entries(groupedAreas).map(([key, value], index) => {

            const { x, y } = calculateButtonPosition(value[0], index);

            return ({
                key, x, y
            })
        });
    }, [groupedAreas, mapSizes.height, mapSizes.width]);

    return (
        <div className='area-contacts__area-wrapper' ref={map}>
            <ul className="area-contacts__area" style={styles} >
                {positions.map((el, index) => {
                    const { key, x, y } = el;

                    return (<li className={`area-contacts__area-item area-contacts__area-item-${index+1}`} key={index} style={{
                        top: `${y}px`,
                        left: `${x}px`
                    }}>
                        <button onClick={() => handleContactClick(index)} className="area-contacts__area-button">
                            <span>{key}</span>
                        </button>
                        <Modal
                            className={groupedAreas[key].length > 2 ? "modal-component--wide" : ""}
                            hasScrollbar={true}
                            hasLargeContent={groupedAreas[key].length > 2}
                            isActive={activeItemId === index}
                            onCloseCallback={handleClose}
                            modalContent={getModalContent(key)}
                        />
                    </li>)
                })}
            </ul>
        </div>
    );
};

export default AreaContactsRoot;